import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import { PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRoles } from '@/hooks/useRoles';
import { resetAllFormStates } from '@/redux/forms/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { useNewRoleState } from './newRole';
import { RoleActionsMenu } from './RoleActionsMenu';
import { useDeleteRoleModal } from './useDeleteRoleModal';

type Props = { roleId: string };

export const RoleEditorBreadcrumb = ({ roleId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { roles, listStatus, updateRole, updateStatus } = useRoles();
  const confirmDelete = useDeleteRoleModal();

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'roles', actions.WRITE));
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.DELETE)
  );

  const { roleName: pendingName } = useNewRoleState(roleId);
  const role = roles.find((r) => r.role_id === roleId);
  const name = pendingName ?? role?.name;
  const listPath = `/${slug}/account/manage/roles`;
  const isLoading = !pendingName && listStatus === 'pending' && roles.length === 0;

  const handleDeleted = () => {
    dispatch(resetAllFormStates());
    navigate(listPath);
  };

  return (
    <PageSubHeaderBreadcrumbs>
      <Button variant="ghost-neutral" size="icon-sm" aria-label={t('common.back')} asChild>
        <Link to={listPath}>
          <ArrowLeftIcon size={16} />
        </Link>
      </Button>
      <div className="mr-4 ml-2 h-4 w-px bg-border" />
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={listPath} className="text-sm leading-5 font-normal">
                {t('permissions.manage_access')}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {isLoading ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <BreadcrumbPage className="text-sm leading-5 font-normal">{name}</BreadcrumbPage>
            )}
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {role?.account_id && (
        <RoleActionsMenu
          name={role.name}
          canWrite={canWrite}
          canDelete={canDelete}
          isRenaming={updateStatus === 'pending'}
          align="start"
          triggerClassName="ml-3"
          onRename={(name, opts) => updateRole({ role_id: roleId, name }, opts)}
          onDelete={() => confirmDelete(role, { onSuccess: handleDeleted })}
        />
      )}
    </PageSubHeaderBreadcrumbs>
  );
};
